import {
  DAY,
  isEmpty,
  isNotDefined,
  MINUTE,
  WEEK,
  type ProgramTaskData,
  type ProgramTaskSchedule,
} from '@terros/common'
import { DateTime } from 'luxon'
import { WEEKDAYS } from '../../competition'
import { type SelectItem } from '../../types'
import { type ProgramTaskDurationOption, type TaskFormDuration } from './types'

const MINUTES_PER_DAY = DAY / MINUTE

export const toFormDuration = ({
  duration,
  openSince = 0,
}: Pick<ProgramTaskData, 'duration' | 'openSince'>): TaskFormDuration => {
  if (isNotDefined(duration)) return { type: 'none', days: 0, time: '00:00' }
  const total = duration + openSince

  if (total === MINUTES_PER_DAY) return { type: 'endOfDay', days: 1, time: '00:00' }
  if (total === MINUTES_PER_DAY * 2) return { type: 'nextDay', days: 1, time: '00:00' }
  if (total === WEEK / MINUTE) return { type: 'week', days: 7, time: '00:00' }

  const days = Math.floor(total / MINUTES_PER_DAY)
  const remainingMinutes = total % MINUTES_PER_DAY
  const time = minutesToTime(remainingMinutes)
  return { type: 'custom', days, time }
}

export const fromFormDuration = (duration: TaskFormDuration, openSince = 0): number | undefined => {
  const baseDuration = getBaseDuration(duration)
  if (isNotDefined(baseDuration)) return undefined
  return baseDuration - openSince
}

const getBaseDuration = (duration: TaskFormDuration): number | undefined => {
  switch (duration.type) {
    case 'none':
      return undefined
    case 'endOfDay':
      return MINUTES_PER_DAY
    case 'nextDay':
      return MINUTES_PER_DAY * 2
    case 'week':
      return MINUTES_PER_DAY * 7
    case 'custom': {
      const { days = 0, time } = duration
      const minutes = timeToMinutes(time) ?? 0
      return days * MINUTES_PER_DAY + minutes
    }
  }
}

export const timeToMinutes = (time: string | undefined): number | undefined => {
  if (!time) return
  const date = DateTime.fromFormat(time, 'HH:mm')
  const diff = date.diff(date.startOf('day'), 'minutes').minutes
  return diff
}

export const minutesToTime = (minutes: number): string => {
  const date = DateTime.now().startOf('day').plus({ minutes })
  return date.toFormat('HH:mm')
}

export const TASK_DURATION_OPTIONS: SelectItem<ProgramTaskDurationOption>[] = [
  { value: 'none', label: 'No due date' },
  { value: 'endOfDay', label: 'End of day' },
  { value: 'nextDay', label: 'End of Next day' },
  { value: 'week', label: 'In one week' },
  { value: 'custom', label: 'Custom time' },
]

export const getWeekdaySuffix = (days: number, schedule: ProgramTaskSchedule): string => {
  if (isEmpty(schedule)) return ''
  if (schedule.type !== 'weekday') return ''

  const isSameDay = schedule.days.length === 1
  if (!isSameDay) return ''

  const day = schedule.days[0].weekday
  const weekday = day + days

  return ` (${WEEKDAYS[(weekday - 1) % 7]})`
}
